<template>
  <v-dialog v-model="open" width="calc(100% - 48px)" persistent>
    <template #activator="{ props: activatorProps }">
      <slot name="activator" :props="activatorProps">
        <v-btn
          v-bind="activatorProps"
          color="primary"
          rounded="xl"
          density="comfortable"
          icon="mdi-shopping"
          :aria-label="title"
        />
      </slot>
    </template>
    <StaffOrderShop
      v-if="open"
      :person-id="personId ?? person?.personId ?? undefined"
      :order-id="orderId"
      :item-type="itemType"
      :membership-id="membershipId"
      :balance-number="balanceNumber"
      :restock-balance-type="restockBalanceType"
      @complete="complete"
      @close="open = false"
    />
  </v-dialog>
</template>

<script setup lang="ts">
import { inject, ref, watch, type Ref } from "vue";
import type { PersonStaff as Person } from "../../../cbi-generator/generated/dto/PersonStaff";
import type { DirtyFlag } from "@/composables/useDirtyFlag";
import { usePersonStore } from "@/stores/persons";
import { useNotificationStore } from "@/stores/notification";
import type { ItemType } from "../../../cbi-generator/generated/dto/ItemType";
import type { BalanceType } from "../../../cbi-generator/generated/dto/BalanceType";
import StaffOrderShop from "./StaffOrderShop.vue";

const props = withDefaults(
  defineProps<{
    title?: string;
    personId?: number;
    orderId?: number;
    itemType?: ItemType;
    membershipId?: number;
    balanceNumber?: string;
    restockBalanceType?: BalanceType;
  }>(),
  { title: "Shop" },
);
const emit = defineEmits<{ complete: []; closed: [] }>();
const open = ref(false);
const person = inject<Ref<Person | null> | undefined>("person", undefined);
const personStore = usePersonStore();
const notification = useNotificationStore();
const flags = [
  "membershipDataState",
  "cardDataState",
  "classDataState",
  "donationDataState",
  "dirtyFlag",
].map((key) => inject<DirtyFlag | undefined>(key, undefined));
watch(open, (value) => {
  if (!value) emit("closed");
});

async function complete() {
  flags.forEach((flag) => flag?.update(true));
  emit("complete");
  const id = props.personId ?? person?.value?.personId;
  if (id == null || person?.value?.personId !== id) return;
  try {
    const refreshed = await personStore.searchPersonById(id, true);
    if (!refreshed) throw new Error("Person no longer exists.");
    if (person.value?.personId === id) person.value = refreshed;
  } catch (failure) {
    notification.evalErrorMessage(
      failure,
      "Order completed, but the person's details could not be refreshed.",
    );
  }
}
</script>
